<template>
  <section
    v-if="items.length"
    ref="scrollContainer"
    :aria-label="carouselAriaLabel"
    class="grid w-full gap-12 overflow-x-auto overflow-y-hidden overscroll-x-contain pl-24 [scrollbar-gutter:auto] supports-[overflow-x:overlay]:[overflow-x:overlay]"
    :class="{
      'grid-flow-col-dense [grid-auto-columns:max-content]': items.length > 1,
    }"
    data-test="carousel"
  >
    <article ref="heroRef" class="relative" :class="BIG_ITEM_SIZE_CLASS">
      <GalleryItemRenderer
        v-if="items[0]"
        :image-aria-label="mainImageAriaLabel"
        is-mobile
        :item="items[0]"
        :review-aria-label
        :video-aria-label
        @image-click="emit('image-click', $event)"
      >
        <template #review="{ review, size }">
          <GalleryMobileReviewSmallItem
            v-if="size === 'small'"
            :aria-label="reviewAriaLabel"
            :review
            @click="handleReviewClick(review)"
            @visible="emit('review-visible', review, 0, items.length)"
          />
          <GalleryReviewItem
            v-else
            :aria-label="reviewAriaLabel"
            :review
            @visible="emit('review-visible', review, 0, items.length)"
          />
        </template>
        <template #video>
          <GalleryVideoItem
            :aria-label="videoAriaLabel"
            is-mobile
            @start="onVideoPlay"
          />
        </template>
      </GalleryItemRenderer>
      <div
        v-if="withSticker"
        class="absolute bottom-8 left-8 empty:hidden"
        role="button"
        tabindex="0"
        @click="handleStickerClick"
        @keypress.enter="handleStickerClick"
      >
        <slot name="sticker" />
      </div>
    </article>

    <ul
      v-if="items.length > 1"
      class="grid grid-flow-col gap-12 h-full w-full list-none overflow-y-hidden overflow-x-visible overscroll-x-auto whitespace-nowrap pr-24 [scrollbar-gutter:auto]"
    >
      <li
        v-for="(item, index) in items.slice(1)"
        :key="getItemKey(item, index + 1, 'mobile')"
        ref="itemRefs"
        class="shrink-0 grow list-none"
        :class="[
          item.size === 'large' ? BIG_GRID_ITEM_CLASS : SMALL_GRID_ITEM_CLASS,
          item.size === 'large' ? BIG_ITEM_SIZE_CLASS : SMALL_ITEM_SIZE_CLASS,
        ]"
      >
        <GalleryItemRenderer
          :image-aria-label="getImageAriaLabel(item, index + 1)"
          is-mobile
          :item
          :review-aria-label
          :video-aria-label
          @image-click="emit('image-click', $event)"
        >
          <template #review="{ review, size }">
            <GalleryMobileReviewSmallItem
              v-if="size === 'small'"
              :aria-label="reviewAriaLabel"
              :review
              @click="handleReviewClick(review)"
              @visible="emit('review-visible', review, index + 1, items.length)"
            />
            <GalleryReviewItem
              v-else
              :aria-label="reviewAriaLabel"
              :review
              @visible="emit('review-visible', review, index + 1, items.length)"
            />
          </template>
          <template #video>
            <GalleryVideoItem
              :aria-label="videoAriaLabel"
              is-mobile
              @start="onVideoPlay"
            />
          </template>
        </GalleryItemRenderer>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import {
  type VNode,
  computed,
  onMounted,
  useTemplateRef,
  watchEffect,
} from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useIntersectionObserver, useScroll } from '@vueuse/core'

import type { ReviewWithCount } from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.types'
import translations from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/ProductDetailPageGallery.translations'
import type {
  ProductDetailPageGalleryImage,
  ProductDetailPageGalleryItem,
} from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/ProductDetailPageGallery.types'
import GalleryItemRenderer from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryItemRenderer.vue'
import GalleryReviewItem from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryReviewItem/GalleryReviewItem.vue'
import GalleryVideoItem from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryVideoItem/GalleryVideoItem.vue'
import marketingVideoTranslations from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/GalleryVideoItem/components/MarketingVideo/MarketingVideo.translations'
import { useGalleryTracking } from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/composables/useGalleryTracking/useGalleryTracking'
import { getItemKey } from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/utils/getItemKey'

import {
  BIG_GRID_ITEM_CLASS,
  BIG_ITEM_SIZE_CLASS,
  SMALL_GRID_ITEM_CLASS,
  SMALL_ITEM_SIZE_CLASS,
} from './GalleryMobile.constants'
import GalleryMobileReviewSmallItem from './components/GalleryMobileReviewSmallItem/GalleryMobileReviewSmallItem.vue'

const props = defineProps<{
  items: ProductDetailPageGalleryItem[]
  trackingProductModel: string
  productName?: string
  withSticker?: boolean
}>()

const emit = defineEmits<{
  (e: 'image-click', image: ProductDetailPageGalleryImage): void
  (e: 'review-click', review: ReviewWithCount): void
  (
    e: 'review-visible',
    review: ReviewWithCount,
    index: number,
    total: number,
  ): void
  (e: 'sticker-click'): void
}>()

const {
  onScrollStart,
  onScrollEnd,
  onSlideImpression,
  onVideoPlay,
  onReviewClick,
} = useGalleryTracking(props.trackingProductModel)

defineSlots<{
  sticker?: (props: { size?: 'small' | 'default' }) => VNode[]
}>()

const i18n = useI18n()

const scrollContainer = useTemplateRef('scrollContainer')
const heroRef = useTemplateRef('heroRef')
const { arrivedState } = useScroll(scrollContainer)

// --- Scroll tracking (fire once) ---
let hasTrackedStart = false
let hasTrackedEnd = false

watchEffect(() => {
  if (import.meta.server) return

  if (!hasTrackedStart && !arrivedState.left) {
    hasTrackedStart = true
    onScrollStart()
  }

  if (!hasTrackedEnd && arrivedState.right) {
    hasTrackedEnd = true
    onScrollEnd()
  }
})

// --- Slide impression tracking (fire once per slide) ---
const itemRefs = useTemplateRef('itemRefs')
const impressedSlides = new Set<number>()

function trackSlideImpressionOnce(index: number) {
  if (impressedSlides.has(index)) return

  impressedSlides.add(index)
  onSlideImpression({
    currentSlide: index,
    totalSlides: props.items.length,
    itemKind: props.items[index].kind,
  })
}

onMounted(() => {
  // Observe the hero item (index 0)
  if (heroRef.value) {
    useIntersectionObserver(
      heroRef,
      ([entry]) => {
        if (entry?.isIntersecting) {
          trackSlideImpressionOnce(0)
        }
      },
      { root: scrollContainer.value, threshold: 0.5 },
    )
  }

  // Observe all remaining items
  itemRefs.value?.forEach((element, index) => {
    useIntersectionObserver(
      element,
      ([entry]) => {
        if (entry?.isIntersecting) {
          trackSlideImpressionOnce(index)
        }
      },
      { root: scrollContainer.value, threshold: 0.5 },
    )
  })
})

// --- Review click tracking ---
function handleReviewClick(review: ReviewWithCount) {
  onReviewClick({ averageRate: review.averageRate, count: review.count })
  emit('review-click', review)
}

function handleStickerClick(e: Event) {
  e.stopPropagation()
  e.preventDefault()

  emit('sticker-click')
}

// --- Aria labels ---
const videoAriaLabel = computed(() => {
  if (!props.productName) return undefined

  return i18n(marketingVideoTranslations.playAriaLabel, {
    productName: props.productName,
  })
})

const reviewAriaLabel = computed(() => {
  if (!props.productName) return undefined

  return i18n(translations.reviewButtonAriaLabel, {
    productName: props.productName,
  })
})

const carouselAriaLabel = computed(() => {
  if (!props.productName) return undefined

  return i18n(translations.carouselAriaLabel, {
    productName: props.productName,
  })
})

const mainImageAriaLabel = computed(() => {
  if (!props.productName) return undefined

  return i18n(translations.mainImageButtonAriaLabel, {
    productName: props.productName,
  })
})

function getImageAriaLabel(
  item: ProductDetailPageGalleryItem,
  overallIndex: number,
): string | undefined {
  if (!props.productName || item.kind !== 'image') return undefined

  let imageIndex = 0
  for (let i = 0; i <= overallIndex; i += 1) {
    if (props.items[i].kind === 'image') imageIndex += 1
  }

  const imageTotal = props.items.filter((i) => i.kind === 'image').length

  return i18n(translations.thumbnailButtonAriaLabel, {
    productName: props.productName,
    current: imageIndex,
    total: imageTotal,
  })
}
</script>
